import { Location } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { EpErrorService, ToastOptions, ToastService } from '@isophi/core-ui';
import { BulkInviteTeacher, TeacherInvitationsService } from '@isophi/mng-api';
import { TranslocoService } from '@jsverse/transloco';
import { NgxSpinnerService } from 'ngx-spinner';
import { BehaviorSubject, take } from 'rxjs';

import { RouterLinkFactory } from '../../../../core/router/router-link.factory';
import { HelperService } from '../../../../core/services/helper.service';
import { UserService } from '../../../../core/services/user.service';
import { snakeToCamel } from '../../../../shared/functions/snakeToCamelCase';
import { getGroupsFromUIRole, USER_ROLE_OPTIONS, UserRoleOption, UserRoleType } from '../../../user/constants/teacher-roles.constants';

interface InviteUserForm {
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  email: FormControl<string>;
  role: FormControl<UserRoleType>;
}

type BeErrors = Array<Partial<Record<keyof InviteUserForm, string>>>;

@Component({
  selector: 'app-invite-users',
  templateUrl: './invite-users.component.html',
  styleUrls: ['./invite-users.component.scss'],
  host: {
    class: 'page-layout',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class InviteUsersComponent implements OnInit {
  private readonly helperService = inject(HelperService);
  private readonly translocoService = inject(TranslocoService);
  private readonly formBuilder = inject(FormBuilder);
  private readonly teacherInvitationsService = inject(TeacherInvitationsService);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);
  private readonly spinner = inject(NgxSpinnerService);
  private readonly userService = inject(UserService);
  protected readonly location = inject(Location);
  protected readonly links = inject(RouterLinkFactory);
  private readonly epErrorService = inject(EpErrorService);

  protected readonly availableRoles: UserRoleOption[] = USER_ROLE_OPTIONS;

  protected readonly selectedKindergarten = this.userService.loggedKindergarten;

  protected readonly inviteUsersForm = this.formBuilder.group({
    users: new FormArray<FormGroup<InviteUserForm>>([]),
  });

  protected readonly beErrorKey = 'beError';
  protected readonly beErrors$ = new BehaviorSubject<BeErrors>([]);

  protected readonly toastsOptions: ToastOptions = {
    delay: 9999999999,
    autohide: true,
    closeButton: true,
  };

  ngOnInit(): void {
    // To trigger change detection on header......
    setTimeout(() => {
      this.helperService.setTitle(null, this.translocoService.translate('users/inviteUsers'));
    }, 0);
    this.addUser();
  }

  addUser() {
    this.inviteUsersForm.controls.users.push(
      new FormGroup<InviteUserForm>({
        firstName: new FormControl('', [Validators.required]),
        lastName: new FormControl('', [Validators.required]),
        email: new FormControl('', [Validators.required, Validators.email]),
        role: new FormControl(UserRoleType.TEACHER, [Validators.required]),
      })
    );
  }

  removeUser(index: number) {
    if (index > this.inviteUsersForm.controls.users.length - 1) {
      return;
    }
    this.inviteUsersForm.controls.users.removeAt(index);
  }

  protected onSubmit(): void {
    if (this.inviteUsersForm.invalid) {
      this.inviteUsersForm.markAllAsTouched();
      return;
    }

    const invitations = this.inviteUsersForm.controls.users.controls.map((userForm) => ({
      firstName: userForm.controls.firstName.value,
      lastName: userForm.controls.lastName.value,
      email: userForm.controls.email.value,
      teacherGroupKeywords: getGroupsFromUIRole(userForm.controls.role.value),
    }));

    const payload: BulkInviteTeacher = {
      invitations,
    };

    this.spinner.show();

    this.teacherInvitationsService
      .teacherInvitationsCreate(payload)
      .pipe(take(1))
      .subscribe({
        next: (response) => {
          if (response.successful > 0) {
            const invitedUsers = this.inviteUsersForm.controls.users.controls.filter((user) =>
              response.results.find((invited) => invited.email === user.controls.email.value)
            );
            const invitedUsersString = invitedUsers
              .map((user) => `${user.controls.lastName.value} ${user.controls.firstName.value}`)
              .join(', ');

            this.toastService.success(
              this.translocoService.translate('users/invitationsSendSuccess', {
                users: invitedUsersString,
              }),
              undefined,
              this.toastsOptions
            );
          }
          if (response.failed > 0) {
            const errorMessage = response.results
              .map((result) => {
                if (result.error) {
                  return `${result.email}: ${result.error.message}`;
                }
                return '';
              })
              .join('           ');

            this.toastService.error(
              errorMessage.trim()
                ? errorMessage
                : this.translocoService.translate('users/invitationsSendErrorPartial', {
                    count: response.failed,
                  }),
              undefined,
              this.toastsOptions
            );
          }
          // Navigate back to users list
          this.router.navigate(this.links.userList());
        },
        error: (errorResponse) => {
          this.spinner.hide();

          if (errorResponse.error.invitations) {
            const newBeErrors = [];
            const invitationsKeys = Object.keys(errorResponse.error.invitations);
            invitationsKeys.forEach((errorKey, index) => {
              const error = errorResponse.error.invitations[errorKey];
              const errorKeys = Object.keys(error);
              const beErrorsGroup: BeErrors[number] = {};

              errorKeys.forEach((errorKey) => {
                const transformedErrorKey = snakeToCamel(errorKey);
                this.inviteUsersForm.controls.users.at(index)?.controls[transformedErrorKey]?.setErrors({ [this.beErrorKey]: true });
                beErrorsGroup[transformedErrorKey] = error[errorKey]?.at(0);
              });

              if (Object.keys(beErrorsGroup).length > 0) {
                newBeErrors.push(beErrorsGroup);
              }
            });

            this.beErrors$.next(newBeErrors);
          }
          this.epErrorService.handleEpErrorResponse(errorResponse, {
            defaultMessage: this.translocoService.translate('users/invitationsSendError'),
            toast: {
              show: true,
              options: this.toastsOptions,
            },
          });
        },
      });
  }
}
